import type { MyIpItem } from './types';
import { getMyIpProfileDisplay } from './profileDisplay';

export interface MyIpSettingDraft extends Record<string, unknown> { key: string; value: string; }
export interface MyIpEditDraft {
  name: string;
  summary: string;
  persona: string;
  languageStyle: string;
  visualStyle: string;
  moreSettings: MyIpSettingDraft[];
}
export type MyIpEditIssue = keyof MyIpEditDraft | 'cover' | 'profile';

/** 历史未知结构不能被空表单覆盖；提示用户重载/核对数据而不是静默丢弃。 */
export function getMyIpEditDraft(item: MyIpItem): MyIpEditDraft {
  const { profile } = item;
  const display = getMyIpProfileDisplay(profile);
  const settings = profile.more_settings ?? [];
  if (!Array.isArray(settings) || settings.some((setting) => !setting || typeof setting !== 'object'
    || Array.isArray(setting) || typeof setting.key !== 'string' || typeof setting.value !== 'string')) {
    throw new Error('Unsupported IP more_settings');
  }
  return {
    name: item.name,
    summary: item.summary,
    persona: typeof profile.persona === 'string' ? profile.persona : display.personality.join('\n'),
    languageStyle: typeof profile.language_style === 'string' ? profile.language_style : display.speechStyle,
    visualStyle: typeof profile.visual_style === 'string' ? profile.visual_style : '',
    moreSettings: settings.map((setting) => ({ ...setting })),
  };
}

/** 名称/简介按当前文案语言限制；视觉风格为可选项，填写后才校验字符数。 */
export function getMyIpTextLength(value: string, words = false): number {
  return words ? value.trim().split(/\s+/u).filter(Boolean).length : Array.from(value).length;
}

/** 只覆盖用户已编辑的已知键，保留主档详情中的 Agent、媒体和未知扩展字段。 */
export function mergeMyIpEditProfile(item: MyIpItem, draft: MyIpEditDraft) {
  const initial = getMyIpEditDraft(item);
  const profile = { ...item.profile };
  if (draft.persona !== initial.persona) profile.persona = draft.persona;
  if (draft.languageStyle !== initial.languageStyle) {
    profile.language_style = draft.languageStyle;
    if (profile.character_setting) {
      profile.character_setting = { ...profile.character_setting, speech_style: draft.languageStyle };
    }
  }
  if (draft.visualStyle !== initial.visualStyle) profile.visual_style = draft.visualStyle;
  if (JSON.stringify(draft.moreSettings) !== JSON.stringify(initial.moreSettings)) {
    profile.more_settings = draft.moreSettings.map((setting) => ({ ...setting }));
  }
  return profile;
}

/** 前端规则比 complete 接口更完整；不截断用户输入或超过 1 MiB 的 profile。 */
export function validateMyIpEdit(item: MyIpItem, draft: MyIpEditDraft, locale: string): MyIpEditIssue[] {
  const issues: MyIpEditIssue[] = [];
  const words = !/^(zh|cn)(-|$)/i.test(locale);
  if (!draft.name.trim() || getMyIpTextLength(draft.name, words) > (words ? 30 : 20)) issues.push('name');
  if (!draft.summary.trim() || getMyIpTextLength(draft.summary, words) > (words ? 200 : 100)) issues.push('summary');
  if (!draft.persona.trim() || getMyIpTextLength(draft.persona) > 1000) issues.push('persona');
  if (!draft.languageStyle.trim() || getMyIpTextLength(draft.languageStyle) > 1000) issues.push('languageStyle');
  if (draft.visualStyle.trim() && getMyIpTextLength(draft.visualStyle) > 1000) issues.push('visualStyle');
  if (draft.moreSettings.length > 5 || draft.moreSettings.some((setting) => !setting.key.trim()
    || !setting.value.trim() || getMyIpTextLength(setting.value) > 1000)) issues.push('moreSettings');
  if (!item.coverAssetId) issues.push('cover');
  // Blob 按 UTF-8 计算大小，与服务端 JSON 字节数一致（中文不是单字节）。
  if (new Blob([JSON.stringify(mergeMyIpEditProfile(item, draft))]).size > 1048576) issues.push('profile');
  return issues;
}
